import axios, { AxiosResponse } from 'axios';
import { 
  HocBu, 
  HocBuDeletedLog, 
  AttendanceViolation, 
  MakeupStatistics,
  ApiResponse
} from '../../../shared/types/database';
import {
  HocBuListParams,
  HocBuListResponse,
  HocBuCreateRequest,
  HocBuUpdateRequest,
  HocBuAccountingUpdateRequest,
  HocBuBatchCreateRequest,
  HocBuBatchCreateResponse,
  HocBuStatisticsRequest,
  HocBuStatisticsResponse,
  AttendanceViolationListParams,
  AttendanceViolationResponse,
  HocBuDeletedLogResponse,
  HocBuStudentListParams,
  HocBuStudentListResponse
} from '../../../shared/types/api';

import { API_CONFIG, buildAPIURL } from '../config/apiConfig';

const API_BASE = buildAPIURL('/hocbu');

export class MakeupService {
  /**
   * Get list of makeup records with filters
   */
  static async getHocBuList(params?: HocBuListParams): Promise<HocBu[]> {
    try {
  const response: AxiosResponse<HocBuListResponse> = await axios.get(API_BASE, { params });
      return response.data.data;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Create new makeup record
   */
  static async createHocBu(data: HocBuCreateRequest): Promise<void> {
    try {
      await axios.post(API_BASE, data);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Update makeup record
   */
  static async updateHocBu(id: number, data: HocBuUpdateRequest): Promise<void> {
    try {
      await axios.put(`${API_BASE}/${id}`, data);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Update makeup record for accounting (only payment status)
   */
  static async updateHocBuAccounting(id: number, data: HocBuAccountingUpdateRequest): Promise<void> {
    try {
      await axios.put(`${API_BASE}/accounting/${id}`, data);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Delete makeup record
   */
  static async deleteHocBu(id: number): Promise<void> {
    try {
      await axios.delete(`${API_BASE}/${id}`);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Create multiple makeup records
   */
  static async createHocBuBatch(data: HocBuBatchCreateRequest): Promise<HocBuBatchCreateResponse['data']> {
    try {
  const response: AxiosResponse<HocBuBatchCreateResponse> = await axios.post(`${API_BASE}/batch`, data);
      return response.data.data;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Get makeup statistics for accounting
   */
  static async getStatistics(params: HocBuStatisticsRequest): Promise<MakeupStatistics> {
    try {
      const response: AxiosResponse<HocBuStatisticsResponse> = await axios.get(`${API_BASE}/statistics`, { params });
      return response.data.data;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Get attendance violations for a student
   */
  static async getAttendanceViolations(params: AttendanceViolationListParams): Promise<AttendanceViolation[]> {
    try {
      const response: AxiosResponse<AttendanceViolationResponse> = await axios.get(`${API_BASE}/attendance-violations`, { params });
      return response.data.data;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Get deleted records log
   */
  static async getDeletedLog(): Promise<HocBuDeletedLog[]> {
    try {
      const response: AxiosResponse<HocBuDeletedLogResponse> = await axios.get(`${API_BASE}/deleted-log`);
      return response.data.data;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Get students by class section
   */
  static async getStudentsByClassSection(params: HocBuStudentListParams): Promise<any[]> {
    try {
      const response: AxiosResponse<HocBuStudentListResponse> = await axios.get(`${API_BASE}/students`, { params });
      return response.data.data;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Register makeup session (legacy support)
   */
  static async registerMakeup(data: HocBuCreateRequest): Promise<{ hocBuID: string }> {
    try {
      const response: AxiosResponse<ApiResponse & { hocBuID: string }> = await axios.post(`${API_BASE}/register`, data);
      return { hocBuID: response.data.hocBuID };
    } catch (error) {

      throw error;
    }
  }

  /**
   * Calculate makeup fee
   */
  static calculateMakeupFee(soTietLT: number, soTietTH: number, coHoaChat: boolean): number {
    const BASE_LT_FEE = 55000; // Lý thuyết mỗi tiết
    const BASE_TH_FEE = 35000; // Thực hành mỗi tiết
    const TH_WITH_CHEMICAL_FEE = 55000; // Thực hành có hóa chất

    const ltFee = soTietLT * BASE_LT_FEE;
    const thFee = soTietTH * (coHoaChat ? TH_WITH_CHEMICAL_FEE : BASE_TH_FEE);
    return ltFee + thFee;
  }

  /**
   * Format date for display
   */
  static formatDate(date: string | Date | null): string {
    if (!date) return '';
    
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '';
      
      const day = dateObj.getDate().toString().padStart(2, '0');
      const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
      const year = dateObj.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {

      return '';
    }
  }

  /**
   * Format date for SQL
   */
  static formatDateForSQL(dateString: string): string | null {
    if (!dateString) return null;
    
    try {
      // If already in DD/MM/YYYY format, convert to YYYY-MM-DD
      if (typeof dateString === 'string' && dateString.includes('/')) {
        const [day, month, year] = dateString.split('/');
        if (day && month && year) {
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      
      // If Date or other string format, try to convert
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0]; // Get YYYY-MM-DD part
      }
    } catch (err) {

    }
    
    return null;
  }

  /**
   * Validate data before sending
   */
  static validateHocBuData(data: HocBuCreateRequest): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.MaHS) errors.push('Mã sinh viên là bắt buộc');
    if (!data.MonHocID) errors.push('Mã môn học là bắt buộc');
    if (!data.LopHocPhanID) errors.push('Mã lớp học phần là bắt buộc');
    if (data.SoTietLT < 0) errors.push('Số tiết LT không được âm');
    if (data.SoTietTH < 0) errors.push('Số tiết TH không được âm');
    if ((data.SoTietLT || 0) + (data.SoTietTH || 0) === 0) errors.push('Phải có ít nhất 1 tiết LT hoặc TH');

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get status color for UI
   */
  static getStatusColor(trangThai: string): string {
    switch (trangThai) {
      case 'Đã đóng tiền':
        return 'success';
      case 'Chưa đóng tiền':
        return 'warning';
      case 'Đã hủy':
        return 'default';
      default:
        return 'processing';
    }
  }

  /**
   * Save makeup list
   */
  static async saveMakeupList(makeupList: any[]): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await axios.post(`${API_BASE}/batch`, { makeupList });
      return { success: true };
    } catch (error: any) {
      console.error('Error saving makeup list:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Lỗi khi lưu danh sách học bù' 
      };
    }
  }

  /**
   * Get form type color for UI
   */
  static getFormTypeColor(hinhThuc: string): string {
    switch (hinhThuc) {
      case 'Học bù':
        return 'blue';
      case 'Học lại':
        return 'orange';
      default:
        return 'default';
    }
  }
} 
